import React, { useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import _ from 'lodash';

import { getAuthUser } from '../../../lib/services/auth.service';
import { SkillComparisonReportContainer } from './skill-comparison-report.container';

const GET_SKILL_COMPARISON = gql`
  query GetSkillComparison($assessmentId: ID!, $ageGroupId: ID!, $companyId: ID!) {
    assessment(_id: $assessmentId) {
      _id
      useMedian
    }
    skillSummaries(assessmentId: $assessmentId, ageGroupId: $ageGroupId) {
      _id
      mean
      meanTime
      median
      medianTime
      best
      bestTime
      skillId
      playerAssessmentId
    }
    playerAssessments(assessmentId: $assessmentId, ageGroupIds: [$ageGroupId], includeArchived: true) {
      _id
      level
      team
      player {
        _id
        firstName
        lastName
        nickname
      }
      position {
        _id
        name
      }
      adjustedScore
      isConcluded
      isHeld
      deletedAt
      unappliedActionsCount
      positionRank
      overallRank
    }
    skills(companyId: $companyId, includeGlobal: true) {
      _id
      name
    }
  }
`;

interface ISkillComparisonReportGraphQL {
  ageGroup: IAgeGroup;
  goalieFilter: 'goalie' | 'other';
}

export const SkillComparisonReportGraphQL: React.FC<ISkillComparisonReportGraphQL> = ({ ageGroup, goalieFilter }) => {
  const user = getAuthUser();
  const { data, error, loading } = useQuery(GET_SKILL_COMPARISON, {
    variables: {
      assessmentId: user?.settings.activeAssessmentId,
      companyId: user?.settings.activeCompanyId,
      ageGroupId: ageGroup._id,
    },
    fetchPolicy: 'cache-and-network'
  });

  const useMedian = useMemo(() => data?.assessment?.useMedian, [data?.assessment?.useMedian]);
  const skills = useMemo(() => (data?.skills ?? []), [data?.skills]);
  const skillSummaries = useMemo(() => (data?.skillSummaries ?? []), [data?.skillSummaries]);
  const playerAssessments = useMemo(() => (data?.playerAssessments.filter((p: IPlayerAssessment) => !p.deletedAt || p.adjustedScore !== "-") ?? []), [data?.playerAssessments]);

  return <SkillComparisonReportContainer
    useMedian={useMedian}
    ageGroup={ageGroup}
    skills={skills}
    skillSummaries={skillSummaries}
    playerAssessments={playerAssessments}
    loading={loading}
    fetchError={Boolean(error)}
    goalieFilter={goalieFilter}
  />;
};

